import React from "react";
import { Box, Button, Container, TextField, withStyles, WithStyles, createStyles, capitalize } from "@material-ui/core";
import logo from "../../../assets/ReadleeLogoRed.png";
import "./SignUpScreen.css";
import FirebaseService from "../../../services/firebase/FirebaseService";
import { Link } from "react-router-dom";
import googleIcon from "../../../assets/icons/google-icon.jpeg";
import cleverIcon from "../../../assets/icons/cleverIcon.png";
import { closeInformationModal, showErrorModal } from "../../Modals/InformationModalReducer";
import LogService from "../../../services/LogService";
import InformationModal from "../../Modals/InformationModal";
import { RootState } from "../../../state/configureStore";
import { connect } from "react-redux";
import ButtonWithLoading from "../../Loading/ButtonWithLoading";
import LoadingButton from "../../Loading/LoadingButton";
import { NAME_MAX_LENGTH, EMAIL_MAX_LENGTH, GENERAL_MAX_LENGTH } from "../../../utils/stringFormatting";
import { Theme } from "@material-ui/core/styles";
import queryString from "query-string";
import Loading from "../../Loading/Loading";
import { RouteComponentProps } from "react-router";

const styles = (theme: Theme) =>
  createStyles({
    inputField: {
      width: "400px",
      [theme.breakpoints.down("xs")]: {
        width: "345px",
      },
    },
    inputShort: {
      width: "195px",
    },
    loginButton: {
      width: "400px",
      textTransform: "none",
      [theme.breakpoints.down("xs")]: {
        width: "345px",
      },
    },
  });

type FieldNames = "firstName" | "lastName" | "email" | "password" | "rePassword" | "general" | "classCode";

type UserTypes = "teacher" | "student" | "";

type SignUpState = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  rePassword: string;
  school: string;
  errors: Map<FieldNames, string>;
  googleLoadingButtonState: LoadingButton;
  cleverLoadingButtonState: LoadingButton;
  classCode: string;
  loading: boolean;
};

type SignUpScreenProps = ReturnType<typeof mapStateToProps> & RouteComponentProps & WithStyles<typeof styles>;
class SignUpScreen extends React.Component<SignUpScreenProps, SignUpState> {
  constructor(props: SignUpScreenProps) {
    super(props);

    this.state = {
      firstName: "",
      lastName: "",
      password: "",
      rePassword: "",
      school: "",
      email: "",
      errors: new Map(),
      googleLoadingButtonState: LoadingButton.BeforeLoading,
      cleverLoadingButtonState: LoadingButton.BeforeLoading,
      classCode: new URLSearchParams(this.props.location.search).get("classCode") || "",
      loading: true,
    };
  }

  changeGoogleLoadingButtonState = (loadingState: LoadingButton) => {
    this.setState({ googleLoadingButtonState: loadingState });
  };

  changeCleverLoadingButtonState = (loadingState: LoadingButton) => {
    this.setState({ cleverLoadingButtonState: loadingState });
  };

  onChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    this.setState(state => {
      const errors = state.errors;
      const key = event.target.id as FieldNames;
      errors.delete(key);
      return {
        ...state,
        [event.target.id]: event.target.value,
        errors,
      };
    });
  };

  setError = (key: FieldNames, value: string) => this.setState(state => ({ errors: state.errors.set(key, value) }));

  // Sign up with Clever
  signUpWithClever = () => {
    this.changeCleverLoadingButtonState(LoadingButton.Loading);
    const client_id = process.env.REACT_APP_ENV === "production" ? "7af57427e1bca7d1dc60" : "fbf100a3149f285ed38a";
    window.location.href = `https://clever.com/oauth/authorize?response_type=code&redirect_uri=${
      this.cleverRedirect
    }&client_id=${client_id}&state=${encodeURIComponent(JSON.stringify({ classCode: this.classCodeQueryParam }))}`;
  };

  signUpWithGoogle = () => {
    FirebaseService.signUpWithGoogle();
  };

  // Sign up normally
  onNext() {
    // Check for errors in form
    this.validateClassCode();
    this.setState(() => ({ errors: new Map() }));
    if (!this.state.firstName.length) {
      this.setError("firstName", "First Name cannot be empty");
    }

    if (!this.state.lastName.length) {
      this.setError("lastName", "Last Name cannot be empty");
    }

    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (!emailRegex.test(this.state.email)) {
      this.setError("email", "Email is invalid");
    }

    if (this.state.password.length < 6) {
      this.setError("password", "Password length must be greater than 6 characters");
    } else if (this.state.password !== this.state.rePassword) {
      this.setError("rePassword", "Passwords do not match");
    } else {
      // check if account is a duplicate
      FirebaseService.checkEmailExists(this.state.email.toLowerCase()).catch(v => {
        if (v["code"] === "auth/wrong-password") {
          showErrorModal(
            "That email address is taken",
            "That email address is already taken, please click sign-in to login using that address.",
          );
          this.setError("general", "Email is already in use");
        } else if (this.state.errors.size === 0) {
          this.createAccount();
        }
      });
    }
  }

  // Create new account via google or email/password
  createAccount() {
    FirebaseService.createAccount(
      this.state.email.toLowerCase(),
      this.state.password,
      this.userTypeQueryParam,
      this.state.school,
      this.state.firstName,
      this.state.lastName,
      this.classCodeQueryParam,
    ).then(() => {
      FirebaseService.login(this.state.email.toLowerCase(), this.state.password)
        .then(() => {
          this.props.history.replace(this.isStudent ? "/Home" : "/Onboarding");
        })
        .catch(() => {
          this.setError("general", "Could not login");
        });
    });
  }

  basicInputForm() {
    return (
      <div>
        <Box height={15} />
        <ButtonWithLoading
          variant="contained"
          style={{ backgroundColor: "white", width: "400px", textTransform: "none" }}
          className="LoginButton"
          img={cleverIcon}
          imgAlt="Clever"
          buttonClickText={"Sign Up With Clever"}
          buttonFinishedText={"Success!"}
          clickHandler={this.signUpWithClever}
          loadingButtonState={this.state.cleverLoadingButtonState}
          changeLoadingButtonState={this.changeCleverLoadingButtonState}
        />
        <Box height={5} />
        <ButtonWithLoading
          variant="contained"
          style={{ backgroundColor: "white", width: "400px", textTransform: "none" }}
          className="LoginButton"
          img={googleIcon}
          imgAlt="Google"
          buttonClickText="Sign Up With Google"
          buttonFinishedText="Success!"
          clickHandler={this.signUpWithGoogle}
          loadingButtonState={this.state.googleLoadingButtonState}
          changeLoadingButtonState={this.changeGoogleLoadingButtonState}
        />
        <Box height={5} />
        <div className="subtext">
          {" "}
          {this.isStudent
            ? "Click here if your teacher is using Google Classroom."
            : "To access your Google Classroom."}
        </div>
        <Box height={15} />
        <div className="center-contents">
          <hr className="small-dropdown" style={{ width: "50px" }} />
          <div className="subtext">Or</div>
          <hr className="small-dropdown" style={{ width: "50px" }} />
        </div>
        <Box height={15} />
        {this.state.errors.has("general") ? <p style={{ color: "red" }}>{this.state.errors.get("general")}</p> : null}
        <div className="center-contents">
          <TextField
            error={this.state.errors.has("firstName")}
            helperText={this.state.errors.get("firstName")}
            value={this.state.firstName}
            className={this.props.classes.inputShort}
            variant="outlined"
            id="firstName"
            label="First Name"
            onChange={this.onChange}
            required
            inputProps={{ maxLength: NAME_MAX_LENGTH }}
          />
          <Box width={10} />
          <TextField
            error={this.state.errors.has("lastName")}
            helperText={this.state.errors.get("lastName")}
            value={this.state.lastName}
            className={this.props.classes.inputShort}
            variant="outlined"
            id="lastName"
            label="Last Name"
            onChange={this.onChange}
            required
            inputProps={{ maxLength: NAME_MAX_LENGTH }}
          />
        </div>
        <Box height={15} />
        <TextField
          error={this.state.errors.has("email")}
          helperText={this.state.errors.get("email")}
          value={this.state.email}
          className={this.props.classes.inputField}
          variant="outlined"
          id="email"
          label={this.isStudent ? "Email" : "Faculty Email"}
          onChange={this.onChange}
          required
          inputProps={{ maxLength: EMAIL_MAX_LENGTH }}
        />
        <Box height={15} />
        <TextField
          error={this.state.errors.has("password")}
          helperText={this.state.errors.get("password")}
          className={this.props.classes.inputField}
          variant="outlined"
          id="password"
          label="Password"
          required
          type="Password"
          autoComplete="current-password"
          onChange={this.onChange}
        />
        <Box height={15} />
        <TextField
          error={this.state.errors.has("rePassword")}
          helperText={this.state.errors.get("rePassword")}
          className={this.props.classes.inputField}
          variant="outlined"
          id="rePassword"
          label="Re-enter Password"
          required
          type="Password"
          autoComplete="current-password"
          onSubmit={() => this.onNext()}
          onChange={this.onChange}
        />

        <Box height={40} />
        <Button
          variant="contained"
          className={this.props.classes.loginButton}
          color="primary"
          onClick={() => this.onNext()}
        >
          Create Account
        </Button>
        <Box height={40} />
        <div className="center-contents">
          <div className="subtext">
            By proceeding you also agree to the &nbsp;
            <a href="https://www.readlee.com/privacy-policy" target="_blank" rel="noreferrer">
              <span className="subtext">Terms of Use</span>
            </a>
          </div>
        </div>
      </div>
    );
  }

  // main page content
  content() {
    // return standard input form
    return this.basicInputForm();
  }

  setUserType = (userType: UserTypes) => () => {
    const parsed = queryString.parse(window.location.search);
    this.props.history.push(`/sign-up?${queryString.stringify({ ...parsed, userType })}`);
  };

  get isStudent() {
    return this.userTypeQueryParam === "teacher" ? false : true;
  }

  get userTypeQueryParam() {
    return new URLSearchParams(this.props.location.search).get("userType") || "";
  }

  get classCodeQueryParam() {
    return new URLSearchParams(this.props.location.search).get("classCode") || "";
  }

  get cleverRedirect() {
    return (
      window.location.origin +
      window.location.pathname +
      "?" +
      queryString.stringify({ userType: this.userTypeQueryParam })
    );
  }

  validateClassCode = async () => {
    if (this.state.classCode === "skipClassCode" || this.userTypeQueryParam === "teacher") return;

    const result = await FirebaseService.checkClassCode(this.state.classCode);
    if (!result.exists) {
      this.setError("classCode", "Class Code does not exist.");
      this.props.history.push(
        `/sign-up?${queryString.stringify({
          ...queryString.parse(window.location.search),
          classCode: "",
        })}`,
      );
      return;
    }

    this.props.history.push(
      `/sign-up?${queryString.stringify({
        ...queryString.parse(window.location.search),
        classCode: this.state.classCode,
      })}`,
    );
  };

  override componentDidMount() {
    // Check for clever code being sent to app
    const params = new URLSearchParams(this.props.location.search);
    const code = params.get("code");

    const classCode = params.get("classCode") || "";
    if (classCode.length > 0) {
      this.validateClassCode();
    }

    if (code) {
      this.changeCleverLoadingButtonState(LoadingButton.Loading);
      FirebaseService.loginWithClever(
        code,
        this.cleverRedirect,
        () => this.props.history.replace("/"),
        classes =>
          this.props.history.replace("/Onboarding", {
            cleverClasses: classes,
          }),
        uid => {
          if (classCode.length > 0) FirebaseService.addUserToClass(classCode, uid);
        },
      )
        .then(() => {
          this.changeCleverLoadingButtonState(LoadingButton.DoneLoading);
        })
        .catch(() => {
          alert("Server issue, please try again later.");
          this.changeCleverLoadingButtonState(LoadingButton.BeforeLoading);
        })
        .finally(() => {
          this.setState({ loading: false });
        });
    } else {
      FirebaseService.finishSignUpWithGoogle()
        .then(res => {
          if (!res.user) return;
          this.changeGoogleLoadingButtonState(LoadingButton.DoneLoading);
          if (res.newUser && !this.isStudent) {
            this.props.history.replace("/Onboarding");
          } else {
            // Reset Sign Up with Google Button
            this.changeGoogleLoadingButtonState(LoadingButton.BeforeLoading);
            this.props.history.replace("/");
          }
        })
        .catch(e => {
          if (e.code === "auth/cancelled-popup-request") {
            showErrorModal(
              "Could not open Google dialog",
              'It looks like you quickly clicked on the "Sign in With Google" button.  ' +
                "When this button is clicked a new window will open up to allow you to select your Google user.  You will need to complete all steps in that " +
                "window before continuing on.",
            );
          } else if (e?.message !== "No user") {
            console.log(e);
            LogService.exception(e, "SignUpScreen.componentDidMount", "Failed to sign up");
          }
          // Reset Sign Up with Google Button
          this.changeGoogleLoadingButtonState(LoadingButton.BeforeLoading);
        })
        .finally(() => {
          this.setState({ loading: false });
        });
    }
  }

  override render() {
    if (this.state.loading) return <Loading />;
    const selectUserTypeStep = !["teacher", "student"].includes(this.userTypeQueryParam);

    const classCodeStep =
      !selectUserTypeStep && this.isStudent && !this.classCodeQueryParam && this.state.classCode !== "skipClassCode";

    return (
      <Container className="SignUpScreen" style={{ backgroundColor: "white" }}>
        <div className="general">
          <form>
            <Box height={80} />
            <img src={logo} alt="Readlee" height={30} />
            <Box height={35} />
            {selectUserTypeStep ? (
              <>
                <div style={{ fontWeight: "bolder", fontSize: "larger" }}>Welcome to Readlee!</div>
                <div style={{ fontWeight: "bolder", fontSize: "larger" }}>What's your role?</div>
              </>
            ) : (
              <div style={{ fontWeight: "bolder", fontSize: "larger" }}>
                Let’s Get You Signed Up With Your {capitalize(this.userTypeQueryParam)} Account!
              </div>
            )}
            <Box height={15} />
            <Box height={15} />
            {selectUserTypeStep && (
              <>
                <Button
                  onClick={this.setUserType("student")}
                  style={{ width: "250px" }}
                  variant="contained"
                  color="primary"
                >
                  Student
                </Button>
                <Box height={15} />

                <Button
                  onClick={this.setUserType("teacher")}
                  style={{ width: "250px" }}
                  variant="contained"
                  color="primary"
                >
                  Teacher
                </Button>
                <br />
                <br />
              </>
            )}

            {classCodeStep && (
              <>
                <TextField
                  error={this.state.errors.has("classCode")}
                  helperText={this.state.errors.get("classCode")}
                  value={this.state.classCode}
                  className={this.props.classes.inputField}
                  variant="outlined"
                  id="classCode"
                  label="Enter Class Code"
                  onChange={this.onChange}
                  inputProps={{ maxLength: GENERAL_MAX_LENGTH }}
                  required
                />
                <br />
                <br />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={this.validateClassCode}
                  className={this.props.classes.loginButton}
                >
                  Next
                </Button>
                <br />

                <div className="center-contents">
                  <Button
                    className="link-text"
                    onClick={() => {
                      this.setState({ classCode: "skipClassCode" });
                    }}
                  >
                    Don't have a Class Code? Click here!
                  </Button>
                </div>
              </>
            )}

            {!selectUserTypeStep && !classCodeStep && this.content()}
          </form>
          <div className="center-contents">
            <div className="subtext">
              Already a user?
              <Button className="subtext">
                <Link
                  className="NavLink"
                  to={`/login${this.classCodeQueryParam ? `?joinClass=${this.classCodeQueryParam}` : ""}`}
                >
                  Sign in
                </Link>
              </Button>
            </div>
          </div>
        </div>

        <InformationModal
          title={this.props.informationModalState.title}
          onOk={closeInformationModal}
          open={this.props.informationModalState.isOpen}
        >
          That email address is already taken. Please click <a href="/Login">here</a> to login using that address.
        </InformationModal>
      </Container>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  informationModalState: state.informationModalState,
});

export default withStyles(styles)(connect(mapStateToProps)(SignUpScreen));
